/**
 * ZoomControls — zoom-in / zoom-out / reset buttons for PlanViewer.
 *
 * Requires a `zoomBridgeRef` whose `.current` exposes the TransformWrapper
 * imperative API (`zoomIn`, `zoomOut`, `resetTransform`). This bridge is
 * wired in Phase C when TransformWrapper is added to PlanViewer.
 *
 * When `zoomBridgeRef` is undefined/null the component returns null so it
 * does not occupy space before Phase C is complete.
 */
import { useProduct } from '../../../index.js'

import { useI18n }    from '../../../../../shared/i18n/useI18n.js'

import { PRODUCT_VIEW_MODE } from '../../../constants/viewModes.js'

import { Button, ButtonGroup } from '../../../../../shared/ui/Button/index.js'
import { Tooltip }             from '../../../../../shared/ui/Tooltip/index.js'
import { ZoomIn }              from '../../../../../shared/ui/icons/ZoomIn.jsx'
import { ZoomOut }             from '../../../../../shared/ui/icons/ZoomOut.jsx'

/**
 * @param {object}                                    props
 * @param {{ current: TransformWrapperRef|null }|undefined} props.zoomBridgeRef
 * @param {boolean}                                   props.isMobile
 */
export function ZoomControls({ isMobile, zoomBridgeRef }) {
  const { t } = useI18n()
  const { modes } = useProduct()

  // Phase C will pass a real bridge; until then the controls are hidden.
  if (!zoomBridgeRef) return null

  // Axonometric mode uses its own drag interaction (frame switching) and does
  // not mount TransformWrapper — zoom is unavailable in that mode.
  const isAxonometric = modes.activeMode === PRODUCT_VIEW_MODE.AXONOMETRIC

  const zoomIn  = () => zoomBridgeRef.current?.zoomIn()
  const zoomOut = () => zoomBridgeRef.current?.zoomOut()

  return (
    <ButtonGroup 
      vertical={isMobile ? false : true}
      size={isMobile ? 'medium' : 'large'}
      aria-label={t('common.zoom')}
    >
      <Tooltip title={t('common.zoom_in')} placement="left" noWrap>
        <Button
          type="button"
          kind="icon"
          size={isMobile ? 'medium' : 'large'}
          aria-label={t('common.zoom_in')}
          onClick={zoomIn}
          disabled={isAxonometric}
        >
          <ZoomIn width="1em" height="1em" />
        </Button>
      </Tooltip>

      <Tooltip title={t('common.zoom_out')} placement="left" noWrap>
        <Button
          type="button"
          kind="icon"
          size={isMobile ? 'medium' : 'large'}
          aria-label={t('common.zoom_out')}
          onClick={zoomOut}
          disabled={isAxonometric}
        >
          <ZoomOut width="1em" height="1em" />
        </Button>
      </Tooltip>
    </ButtonGroup>
  )
}
